import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslocoService } from '@jsverse/transloco';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridOptions, SelectionChangedEvent } from 'ag-grid-community';

import { DeviceDetection } from '@shure/cloud/shared/models/devices';
import { BaseTableComponent } from '@shure/cloud/shared/ui/components';
import { PipesModule } from '@shure/cloud/shared/ui/pipes';
import { compareStrings } from '@shure/shared/angular/ui/comparators/string';
import {
	AgGridResponsiveWrapperComponent,
	areDeviceNameCellsEqual,
	DeviceNameCellData,
	DeviceNameRendererComponent,
	DeviceNameRendererParams,
	makeComparator
} from '@shure/shared/angular/ui/components/ag-grid-next';

import {
	mapDeviceDetectionDataToLocalNetworkTableRowData,
	LocalNetworkTableRowData
} from '../../../../../models/local-network-row-data';
import { SelectedLocalNetworkDeviceIdMap } from '../../../../../models/step2-discover-devices.models';

interface LocalNetworkTableContext {
	componentParent: LocalNetworkTableComponent;
}

@Component({
	selector: 'sh-local-network-table',
	imports: [AgGridAngular, AgGridResponsiveWrapperComponent, CommonModule, MatProgressBarModule, PipesModule],
	templateUrl: './local-network-table.component.html',
	styleUrls: ['./local-network-table.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LocalNetworkTableComponent extends BaseTableComponent<LocalNetworkTableRowData> {
	@Output() public selectionChange = new EventEmitter<SelectedLocalNetworkDeviceIdMap>();

	@Input()
	public set devices(_devices: DeviceDetection[] | null) {
		this.gridData = mapDeviceDetectionDataToLocalNetworkTableRowData(_devices ?? []);
	}

	// AG Grid Members
	public gridData: LocalNetworkTableRowData[] = [];

	public context: LocalNetworkTableContext = {
		componentParent: this
	};

	public readonly columnDefs: ColDef<LocalNetworkTableRowData>[] = [
		//
		// checkbox selector column
		//
		this.createCheckboxColumn({
			cellClass: (params) => [
				'local-network-table-checkbox-cell',
				`local-network-table-checkbox-cell-${params.data?.id}`
			]
		}),
		//
		// Device Name: ICON, Name
		//
		this.createCustomRendererColumn({
			field: 'nameCellData',
			headerName: this.translocoService.translate('common.labels.device-name'),
			cellRenderer: DeviceNameRendererComponent,
			cellRendererParams: {
				shouldHideStateBar: true,
				renderDeviceNameAsText: true
			} satisfies DeviceNameRendererParams,
			cellClass: 'sh-device-name-cell-renderer',
			lockVisible: true,
			filter: true,
			filterValueGetter: (params): string | undefined => params.data?.nameCellData.name.name,
			equals: areDeviceNameCellsEqual,
			comparator: makeComparator<DeviceNameCellData>((a, b) => compareStrings(a?.name.name, b?.name.name)),
			getQuickFilterText: (params) => params.data.nameCellData.name.name.toLowerCase(),
			valueParser: (params): string | undefined => params.data.nameCellData.name.name,
			minWidth: 250
		}),
		//
		// Model
		//
		this.createTextColumn({
			field: 'model',
			headerName: this.translocoService.translate('common.labels.device-model')
		}),
		//
		// IP Address
		//
		this.createIpAddressColumn({
			field: 'ipAddress'
		}),
		//
		// Firmware
		//
		this.createTextColumn({
			field: 'firmwareVersion',
			headerName: this.translocoService.translate('common.labels.firmware-version')
		}),
		//
		// MAC Address
		//
		this.createMacAddressColumn({
			field: 'macAddress'
		})
	];

	constructor(private readonly translocoService: TranslocoService) {
		super(translocoService);
		this.context.componentParent = this;
	}

	public getRowId(params: GetRowIdParams<LocalNetworkTableRowData>): string {
		return params.data.id;
	}

	public onSelectionChanged(params: SelectionChangedEvent<LocalNetworkTableRowData>): void {
		this.emitSelectionChange(params.api.getSelectedRows());
	}

	protected override getGridOptions(): GridOptions<LocalNetworkTableRowData> {
		return {
			...super.getGridOptions(),
			// Attaching the AG Grid popups to the document body currently does not work with Prism dialogs.
			// For now we'll attach to the grid and take the hit on menus getting cut off on smaller tables.
			// TODO: remove this once Prism dialogs have better AG Grid support
			popupParent: undefined
		};
	}

	private emitSelectionChange(selectedRows: LocalNetworkTableRowData[]): void {
		this.selectionChange.emit({
			allSelected: selectedRows.length > 0 && selectedRows.length === this.gridData.length,
			devices: selectedRows.reduce(
				(obj, row) => ({
					...obj,
					[row.id]: row
				}),
				{}
			)
		});
	}
}
